import {html} from 'lit';
import {customElement, property, state} from 'lit/decorators.js';
import {FlywheelComponent} from '@gl/lit/lib/component.js';
import {ImmutableComponent} from '@gl/lit/components/mixins/immutable_component/index.js';
import type {Constructor} from '@gl/lit/types/classes.js';
import type {Model} from './architecture.js';
import {Msg, main, PROVIDER_STATES} from './architecture.js';
import styles from './style.scss';
import type {MatchFieldResponse} from '@gl/types/destinations.js';
import {destinationsService} from '@gl/lit/services/destinations.js';
import {ContextProvider, provide} from '@lit-labs/context';
import type {AudienceDestinationContextData} from '@gl/lit/contexts/audience_destinations.js';
import {AudienceDestinationsContext} from '@gl/lit/contexts/audience_destinations.js';
import type {AudienceDatasetIds} from '@gl/lit/contexts/audience.js';
import {AudienceContext} from '@gl/lit/contexts/audience.js';
import type {Nullable} from '@gl/lit/types/general.js';
import {wrapPromise} from '@gl/lit/lib/http/immutable_fetcher.js';
import type {ScrubList} from '@gl/lit/contexts/scrub_list.js';
import {ScrubListContext} from '@gl/lit/contexts/scrub_list.js';
import type {AudienceExportController} from '@gl/lit/controllers/audience_export_controller/audience_export_controller.js';
import {audienceExportController} from '@gl/lit/controllers/audience_export_controller/audience_export_controller.js';
import {stateController} from '@gl/lit/controllers/state_controller/state_controller_decorators.js';
import {DefaultPersonalizationsContext} from '@gl/lit/contexts/default_personalization_fields.js';

import {DatasetsContext} from '@gl/lit/contexts/datasets.js';

export const DESTINATIONS_PROVIDER_ELEMENT_TAG = 'destinations-provider';

/**
 * Provider for destination data
 */
@customElement(DESTINATIONS_PROVIDER_ELEMENT_TAG)
@stateController(audienceExportController)
export default class DestinationsProvider extends ImmutableComponent<
  Constructor<FlywheelComponent>,
  Model,
  Msg
>({component: FlywheelComponent, main}) {
  static get styles() {
    return [styles];
  }
  controller!: AudienceExportController;
  datasetsContext = new ContextProvider(this, DatasetsContext, []);

  @provide({context: ScrubListContext})
  @property({type: Array})
  scrubList: ScrubList[] = [];

  @provide({context: DefaultPersonalizationsContext})
  @property({type: Array})
  defaultPersonalizations: string[] = [];

  @state()
  destinationData = new ContextProvider(
    this,
    AudienceDestinationsContext,
    this.getDestinationData()
  );

  @provide({context: AudienceContext})
  @property({type: Object})
  audience: Nullable<AudienceDatasetIds> = null;

  @property({type: Array})
  get datasets() {
    return this.model.datasets;
  }

  set datasets(value) {
    this.handleUpdate(Msg.setDatasets(value));
  }

  @property({type: Array})
  get ingests() {
    return this.model.ingests;
  }

  set ingests(value) {
    this.handleUpdate(Msg.setIngests(value));
  }

  @property({type: Array})
  get platforms() {
    return this.model.platforms;
  }

  set platforms(value) {
    this.handleUpdate(Msg.setPlatforms(value));
  }

  private getDestinationData(): AudienceDestinationContextData {
    return Object.freeze({
      destinations: this.model.destinations,
      isLoading: () => this.model.state === PROVIDER_STATES.LOADING,
      getAll: () => structuredClone(this.model.destinations),
      getById: (id: number) => this.model.destinations.get(id),
      getByTeamId: (id: number) => {
        return Object.values(this.model.destinations).find(
          destination => destination.team_id === id
        );
      },
    });
  }

  beforeLoad() {
    this.getContextData();
  }

  getContextData() {
    wrapPromise<MatchFieldResponse>(this, {
      promise: destinationsService.getMatchFields(),
      handler: Msg.setDestinations,
    });
  }

  render() {
    return html`<slot></slot>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [DESTINATIONS_PROVIDER_ELEMENT_TAG]: DestinationsProvider;
  }
}
